import { useEffect, useState } from "react";
import { Box, Paper, TableContainer, TextField } from "@mui/material";
import { useAppDispatch } from "../../redux/store";
import { setErrorObj } from "../redux/errorReducer";
import { T } from "react-translator-component";
import LoadingModal from "../../components/LoadingModal";
import Separator from "../../components/Separator";
import { colors } from "../../common/colors";
import { styles } from "../../pages/styles";
import "../../App.css";
import AdminPageFrame from "../components/AdminPageFrame";
import DropDown from "../components/DropDown";
import FetchButton from "../components/FetchButton";
import PostCard from "../components/post/PostCard";
import UserPostsHeader from "../components/post/postCard/UserPostsHeader";
import ReadMore from "../components/ReadMore";
import apiServices from "../services/apiServices";

type SortType = "created" | "commnet"
type OrderType = "asc" | "desc"

type Option = {
  value: string,
  name: string,
}

export default function EveryonePostList() {
  const sortOptions: Option[] = [
    { value: "created_desc", name: T("sort_by_new") },
    { value: "created_asc", name: T("sort_by_old") },
    { value: "comment_desc", name: T("sort_by_most_comments") },
    { value: "comment_asc", name: T("sort_by_least_comments") },
  ]

  const initialOption = { value: "", name: T("mng_all") }

  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [categoryOptionList, setCategoryOptionList] = useState<Option[]>([initialOption]);
  const [groupOptionList, setGroupOptionList] = useState<Option[]>([initialOption]);
  const [postReportList, setPostReportList] = useState<any[]>([]);
  const [selectSortOption, setSelectSortOption] = useState<string>("created_desc");
  const [selectCategory, setSelectCategory] = useState<string>("");
  const [selectGroup, setSelectGroup] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState<string>("");

  /* API関連 */
  const [posts, setPosts] = useState<any[]>([]);
  const [lastParams, setLastParams] = useState<any>();
  const [pagination, setPagination] = useState<any>({});

  const apiCalls = async () => {
    try {
      setIsLoading(true)

      const resCategories = await apiServices.getCategories()
      const categories = resCategories.categories.map((item: { id: any; name: any }) => {
        return {
          value: item.id,
          name: item.name,
        };
      });
      setCategoryOptionList([initialOption, ...categories])

      const resGroups = await apiServices.getAllGroups()
      const groups = resGroups.groups.map((item: { id: any; name: any; posts_count: any }) => {
        return {
          value: item.id,
          name: item.name,
        };
      });
      setGroupOptionList([initialOption, ...groups])
    } catch (error: any) {
      dispatch(setErrorObj(error))
    } finally {
      setIsLoading(false)
    }
  }

  const getMyReportList = async () => {
    try {
      setIsLoading(true)
      const res = await apiServices.getMyPostReportList()
      setPostReportList(res.post_reports)
    } catch (error: any) {
      dispatch(setErrorObj(error))
    } finally {
      setIsLoading(false)
    }
  }

  const getEveryonePosts = async (params: any) => {
    try {
      setIsLoading(true);
      const res = await apiServices.getEveryonePosts(params);
      if (params.page === 1) {
        setPosts(res.posts)
      } else {
        setPosts([...posts, ...res.posts])
      }
      setPagination(res.pagination)
      setLastParams(params);
    } catch (error: any) {
      dispatch(setErrorObj(error))
    } finally {
      setIsLoading(false);
    }
  }

  const createParams = (page?: number) => {
    const sortOrder = parseSortOrder()
    const params: any = {
      term: searchTerm,
      page: page ?? 1,
      sort_type: sortOrder.sortType,
      order: sortOrder.order,
      per_page: 12
    }
    const categotyId = Number(selectCategory)
    const groupId = Number(selectGroup)
    if (categotyId > 0) params.category_id = categotyId
    if (groupId > 0) params.group_id = groupId

    return params
  }

  useEffect(() => {
    apiCalls()
    getMyReportList()

    const params = createParams()
    getEveryonePosts(params)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loadMore = () => {
    if (!pagination?.next_page || pagination?.next_page < 2) return

    const params = { ...lastParams, page: pagination.next_page}
    getEveryonePosts(params);
  };

  const handleChangeSearchTerm = (value: string) => {
    setSearchTerm(value);
  }

  const parseSortOrder = () => {
    const [ type, direction ] = selectSortOption.split("_");
    const sortType = type || "created"
    const orderType = direction || "desc"

    return {
      sortType: sortType as SortType,
      order: orderType as OrderType,
    }
  }

  const handleClickTag = (tag: string) => {
    handleChangeSearchTerm(tag)
  }

  const handleSearch = async () => {
    const params = createParams()
    await getEveryonePosts(params)
    return false
  }

  return (
    <AdminPageFrame>
      <LoadingModal visible={isLoading} />

      <TableContainer component={Paper} sx={style.searchContainer}>
        <div style={style.searchTitle}>{T("mng_search_area_title")}</div>

        <div style={style.searchInputBox}>
          <TextField
            placeholder={`${T("mng_search_everyone_posts_placeholder")}`}
            value={searchTerm}
            onChange={(e) => handleChangeSearchTerm(e.target.value)}
            style={style.searchInputForm}
          />
        </div>

        <div style={style.selectAndBtnBox}>
          <div style={style.dropDownsBox}>
            <DropDown
              title={T("mng_category")}
              value={selectCategory}
              options={categoryOptionList}
              onChange={(e: any) => setSelectCategory(e.target.value)}
              sx={style.w30}
            />

            <DropDown
              title={T("mng_group")}
              value={selectGroup}
              options={groupOptionList}
              onChange={(e: any) => setSelectGroup(e.target.value)}
              sx={style.w30}
            />

            <DropDown
              title={T("mng_sort")}
              value={selectSortOption}
              options={sortOptions}
              onChange={(e: any) => setSelectSortOption(e.target.value)}
              sx={style.w30}
            />
          </div>

          <Box sx={style.btncontainer}>
            <FetchButton
              title={T("mng_search")}
              onSubmit={handleSearch}
            />
          </Box>
        </div>
      </TableContainer>

      <div style={{...styles.communityContainer, marginTop: 20}}>
        <TableContainer component={Paper} sx={{ minWidth: 650, overflowX: "visible", margin: "20px 0" }}>
          <div aria-label="caption table">
            <div style={style.tableHeader}>
              <div style={style.tableTitle}>{T("mng_posts_everyone_posts")}</div>
            </div>
            <Separator color={colors.farm_gray100} />
          </div>

          <div style={style.postList}>
            {posts.map((post: any, index: number) => (
              <PostCard
                post={post}
                header={
                  <UserPostsHeader
                    post={post}
                    postReports={postReportList}
                    onChangePostReports={getMyReportList}
                  />
                }
                onClickTag={handleClickTag}
                key={index}
              />
            ))}
          </div>

          <ReadMore open={pagination.next_page > 0} sx={style.paginate} onClick={loadMore} />
        </TableContainer>

      </div>
    </AdminPageFrame>
  );
}

const style = {
  searchContainer: {
    marginBottom: "10px",
    padding: "20px",
  },

  searchTitle: {
    padding: "10px 0",
  },

  dropDownsBox: {
    display: "flex",
    margin: "10px 0 0 10px",
    gap: "20px",
    flex: 1
  },

  searchInputForm: {
    width: "100%",
    marginBottom: "20px"
  },

  searchInputBox: {
    margin: "10px 20px"
  },

  selectAndBtnBox: {
    margin: "10px",
    display: "flex"
  },

  w30: {
    width: "30%"
  },

  tableTitle: {
    fontWeight: 700,
    fontSize: 16,
    lineHeight: "120px",
    paddingLeft: 10,
  },

  paginate: {
    flexDirection: "row" as const,
    justifyContent: "center",
    alignItems: "center",
    display: "flex",
    width: "100%"
  },

  tableHeader: {
    display: "flex",
    justifyContent: "space-between",
    padding: "0 20px",
  },

  postList: {
    display: "flex",
    flexWrap: "wrap" as const,
    gap: 20,
  },

  btncontainer: {
    padding: 0,
    display: "flex",
    alignItems: "flex-end",
  },

  createButton: {
    height: "fit-content",
    margin: "auto 0",
  },
}